import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  formatPhone,
  labelFitsSport,
  LEAD_STATUS_WORDS,
  leadStatuses,
  UAE_EMIRATES,
  type AdminLead,
  type AdminLeadLabel,
  type LeadSport,
  type LeadStatus,
  type UaeEmirate,
} from "@teamna/shared";
import { useMemo, useState, type FormEvent } from "react";
import {
  Button,
  ButtonLink,
  Checkbox,
  ConfirmDialog,
  CopyMessageButton,
  DataTable,
  ErrorState,
  Field,
  InlineError,
  PageHeader,
  Panel,
  SegmentedControl,
  Select,
  SelectField,
  TextAreaField,
  Toolbar,
  showToast,
  type DataTableColumn,
} from "@teamna/ui/components/index.ts";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import {
  useChangeLeadLabel,
  useCreateLead,
  useDeleteLead,
  useLeadLabels,
  useLeadRuns,
  useLeads,
  useUpdateLead,
} from "../lib/leads-api.ts";
import { addedByText, instagramLink, LabelChip, LeadsTabs, runTitle, ScoreValue, SPORT_WORDS, StatusBadge, whatsAppLink } from "./leads-parts.tsx";

/**
 * The Leads table, `/leads` (strategy repo: teamna/strategy/lead-finder-plan.html): every lead with
 * its labels and score, highest score first. A chip's × takes a label off; "Add a label" puts one
 * on. The score is the sum of the label scores, so it moves at once. A row opens the lead.
 *
 * `?run=<id>` opens the table on one run and `?lead=<id>` opens one lead: the links the connector
 * gives Claude.
 */

const onError = (error: Error) => showToast(copyForError(error).message, { tone: "info" });

type LeadsSearch = { run?: string; lead?: string };

/** The search of the page, kept to the two strings it knows (the routers validate no search). */
function readSearch(raw: Record<string, unknown>): LeadsSearch {
  return {
    ...(typeof raw.run === "string" ? { run: raw.run } : {}),
    ...(typeof raw.lead === "string" ? { lead: raw.lead } : {}),
  };
}

const ALL = "all";

export function LeadsScreen() {
  const search = readSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [sport, setSport] = useState<LeadSport | typeof ALL>(ALL);
  const [status, setStatus] = useState<LeadStatus | typeof ALL>(ALL);
  const [labelId, setLabelId] = useState<string>(ALL);
  const [adding, setAdding] = useState(false);

  const query = useMemo(
    () => ({
      ...(search.run ? { runId: search.run } : {}),
      ...(sport !== ALL ? { sport } : {}),
      ...(status !== ALL ? { status } : {}),
      ...(labelId !== ALL ? { labelId } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
    }),
    [search.run, sport, status, labelId, q],
  );
  const leads = useLeads(query);
  const labels = useLeadLabels();
  const runs = useLeadRuns();
  const open = leads.data?.find((lead) => lead.id === search.lead) ?? null;

  const go = (next: LeadsSearch) => void navigate({ to: "/leads", search: next as never });
  const setRun = (run: string) => go({ ...(run !== ALL ? { run } : {}) });
  const openLead = (leadId: string | undefined) => go({ ...(search.run ? { run: search.run } : {}), ...(leadId ? { lead: leadId } : {}) });

  const columns = useMemo(() => leadColumns(labels.data ?? []), [labels.data]);
  const strong = leads.data?.filter((lead) => lead.score >= 50).length ?? 0;

  return (
    <>
      <PageHeader
        title="Leads"
        description="Swim academies and pilates studios Teamna may sell to. The score is the sum of the label scores."
        actions={
          <Button onClick={() => setAdding(true)} size="md">
            Add lead
          </Button>
        }
      />
      <LeadsTabs current="leads" />
      <Toolbar search={{ value: q, onChange: setQ, placeholder: "Search name, area, handle or number" }}>
        <Select label="Run" value={search.run ?? ALL} onChange={(event) => setRun(event.target.value)}>
          <option value={ALL}>All runs</option>
          {(runs.data ?? []).map((run) => (
            <option key={run.id} value={run.id}>
              {runTitle(run)}
            </option>
          ))}
        </Select>
        <Select label="Sport" value={sport} onChange={(event) => setSport(event.target.value as LeadSport | typeof ALL)}>
          <option value={ALL}>All sports</option>
          <option value="swimming">Swimming</option>
          <option value="pilates">Pilates</option>
        </Select>
        <Select label="Status" value={status} onChange={(event) => setStatus(event.target.value as LeadStatus | typeof ALL)}>
          <option value={ALL}>Any status</option>
          {leadStatuses.map((value) => (
            <option key={value} value={value}>
              {LEAD_STATUS_WORDS[value]}
            </option>
          ))}
        </Select>
        <Select label="Label" value={labelId} onChange={(event) => setLabelId(event.target.value)}>
          <option value={ALL}>Any label</option>
          {(labels.data ?? []).map((label) => (
            <option key={label.id} value={label.id}>
              {label.name}
            </option>
          ))}
        </Select>
      </Toolbar>
      {leads.error ? (
        <ErrorState error={leads.error} onRetry={() => void leads.refetch()} />
      ) : (
        <>
          {leads.data ? (
            <p className="mb-2 text-[13px] text-ink-2">
              {leads.data.length} {leads.data.length === 1 ? "lead" : "leads"}, {strong} with a score of 50 or more
            </p>
          ) : null}
          <DataTable
            columns={columns}
            rows={leads.data ?? []}
            rowKey={(row) => row.id}
            rowLabel={(row) => row.name}
            onRowClick={(row) => openLead(row.id)}
            isLoading={leads.isLoading}
            ranked
            density="compact"
            countLabel={(count) => `${count} ${count === 1 ? "lead" : "leads"}`}
            empty={{
              title: "No leads here",
              message: search.run || q || sport !== ALL || status !== ALL || labelId !== ALL ? "Nothing matches these filters." : "Add a lead you know, push one from Claude on your phone, or start a small run from your laptop.",
            }}
          />
        </>
      )}
      <LeadPanel lead={open} labels={labels.data ?? []} onClose={() => openLead(undefined)} />
      <AddLeadPanel open={adding} labels={labels.data ?? []} onClose={() => setAdding(false)} onAdded={(lead) => openLead(lead.id)} />
    </>
  );
}

function leadColumns(labels: AdminLeadLabel[]): DataTableColumn<AdminLead>[] {
  return [
    {
      id: "place",
      header: "Place",
      sortValue: (row) => row.name,
      cell: (row) => (
        <div className="min-w-[160px]">
          <p className="font-semibold text-ink" dir="auto">
            {row.name}
          </p>
          <p className="text-[12px] text-ink-2">
            {SPORT_WORDS[row.sport]} · {[row.area, row.emirate].filter(Boolean).join(", ")}
          </p>
          <p className="text-[12px] text-ink-2">{row.run ? `Run #${row.run.number}` : row.source === "chat" ? "From a chat" : "Added by hand"}</p>
        </div>
      ),
    },
    {
      id: "contact",
      header: "Contact",
      hideOnPhone: true,
      cell: (row) => (
        <div className="text-[12px] text-ink-2">
          {row.phones.map((phone) => (
            <p key={phone.e164} className="tabular-nums">
              {formatPhone(phone.e164)}
            </p>
          ))}
          {row.instagram ? <p>@{row.instagram}</p> : null}
        </div>
      ),
    },
    {
      id: "labels",
      header: "Labels",
      cell: (row) => <LeadLabels lead={row} labels={labels} />,
    },
    { id: "score", header: "Score", align: "end", sortValue: (row) => row.score, cell: (row) => <ScoreValue score={row.score} /> },
    { id: "status", header: "Status", sortValue: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
  ];
}

/** The chips of a lead and the picker that adds one. Clicks here do not open the row. */
function LeadLabels({ lead, labels }: { lead: AdminLead; labels: AdminLeadLabel[] }) {
  const change = useChangeLeadLabel();
  const on = new Set(lead.labels.map((label) => label.labelId));
  const addable = labels.filter((label) => !on.has(label.id) && labelFitsSport(label.sport, lead.sport));
  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="flex max-w-[460px] min-w-[220px] flex-wrap items-center gap-1.5" onClick={(event) => event.stopPropagation()}>
      {lead.labels.map((label) => (
        <LabelChip
          key={label.labelId}
          label={label}
          busy={change.isPending}
          onRemove={() => change.mutate({ leadId: lead.id, change: { labelId: label.labelId, action: "remove" } }, { onError })}
        />
      ))}
      {addable.length ? (
        <div className="w-[150px]">
          <Select
            label={`Add a label to ${lead.name}`}
            value=""
            disabled={change.isPending}
            onChange={(event) => {
              if (event.target.value) change.mutate({ leadId: lead.id, change: { labelId: event.target.value, action: "add" } }, { onError });
            }}
          >
            <option value="">+ Label</option>
            {addable.map((label) => (
              <option key={label.id} value={label.id}>
                {label.name}
                {label.score !== null ? ` (${label.score > 0 ? "+" : ""}${label.score})` : ""}
              </option>
            ))}
          </Select>
        </div>
      ) : null}
    </div>
  );
}

// --- one lead -------------------------------------------------------------------------------

function LeadPanel({ lead, labels, onClose }: { lead: AdminLead | null; labels: AdminLeadLabel[]; onClose: () => void }) {
  return (
    <Panel open={lead !== null} onClose={onClose} title={lead?.name ?? "Lead"} subtitle={lead ? `${SPORT_WORDS[lead.sport]} · ${[lead.area, lead.emirate].filter(Boolean).join(", ")}` : undefined} width="lg">
      {lead ? <LeadDetail key={lead.id} lead={lead} labels={labels} onDeleted={onClose} /> : null}
    </Panel>
  );
}

function LeadDetail({ lead, labels, onDeleted }: { lead: AdminLead; labels: AdminLeadLabel[]; onDeleted: () => void }) {
  const update = useUpdateLead();
  const remove = useDeleteLead();
  const [note, setNote] = useState(lead.note ?? "");
  const [followUp, setFollowUp] = useState(lead.followUpOn ?? "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const removed = labels.filter((label) => lead.removedLabelIds.includes(label.id));

  const save = (changes: Parameters<typeof update.mutate>[0]["changes"], words: string) =>
    update.mutate({ leadId: lead.id, changes }, { onSuccess: () => showToast(words), onError });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[12px] font-semibold tracking-[0.04em] text-ink-2 uppercase">Score</p>
          <ScoreValue score={lead.score} />
        </div>
        <div className="w-[200px]">
          <SelectField label="Status" size="compact" value={lead.status} onChange={(event) => save({ status: event.target.value as LeadStatus }, "Status saved.")}>
            {leadStatuses.map((value) => (
              <option key={value} value={value}>
                {LEAD_STATUS_WORDS[value]}
              </option>
            ))}
          </SelectField>
        </div>
      </div>

      <section className="flex flex-col gap-2">
        <h3 className="text-[14px] font-semibold text-ink">Contact</h3>
        {lead.phones.length === 0 && !lead.instagram ? <p className="text-[13px] text-ink-2">No contact yet.</p> : null}
        {lead.phones.map((phone) => (
          <div key={phone.e164} className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] text-ink tabular-nums">{formatPhone(phone.e164)}</span>
            <CopyMessageButton text={phone.e164} label="Copy number" />
            <ButtonLink href={whatsAppLink(phone.e164)} variant="whatsapp" size="sm" target="_blank" rel="noreferrer">
              Open WhatsApp
            </ButtonLink>
            {phone.sourceUrl ? (
              <a href={phone.sourceUrl} target="_blank" rel="noreferrer" className="text-[12px] text-accent-strong underline">
                Where it was seen
              </a>
            ) : null}
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          {lead.instagram ? (
            <ButtonLink href={instagramLink(lead.instagram)} variant="secondary" size="sm" target="_blank" rel="noreferrer">
              Open Instagram
            </ButtonLink>
          ) : null}
          {lead.facebook ? (
            <ButtonLink href={`https://www.${lead.facebook}`} variant="secondary" size="sm" target="_blank" rel="noreferrer">
              Open Facebook
            </ButtonLink>
          ) : null}
          {lead.website ? (
            <ButtonLink href={/^https?:\/\//.test(lead.website) ? lead.website : `https://${lead.website}`} variant="secondary" size="sm" target="_blank" rel="noreferrer">
              Open website
            </ButtonLink>
          ) : null}
          {lead.mapsUrl ? (
            <ButtonLink href={lead.mapsUrl} variant="secondary" size="sm" target="_blank" rel="noreferrer">
              Open Google Maps
            </ButtonLink>
          ) : null}
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-[14px] font-semibold text-ink">Labels</h3>
        <LeadLabels lead={lead} labels={labels} />
        <ul className="flex flex-col gap-1.5 text-[13px] text-ink-2">
          {lead.labels.map((label) => (
            <li key={label.labelId}>
              <span className="font-semibold text-ink">{label.name}</span>: added by {addedByText(label)}
              {label.reason ? <span dir="auto">. {label.reason}</span> : null}
              {label.link ? (
                <>
                  {" "}
                  <a href={label.link} target="_blank" rel="noreferrer" className="text-accent-strong underline">
                    Seen here
                  </a>
                </>
              ) : null}
            </li>
          ))}
        </ul>
        {removed.length ? <p className="text-[13px] text-ink-2">Taken off by a person, so runs leave them off: {removed.map((label) => label.name).join(", ")}.</p> : null}
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-[14px] font-semibold text-ink">Follow-up</h3>
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-[200px]">
            <Field label="Follow up on" type="date" size="compact" value={followUp} onChange={(event) => setFollowUp(event.target.value)} />
          </div>
          <Button variant="secondary" size="sm" onClick={() => save({ followUpOn: followUp || null }, followUp ? "Follow-up saved." : "Follow-up cleared.")}>
            Save date
          </Button>
        </div>
        <TextAreaField label="Note" value={note} onChange={(event) => setNote(event.target.value)} rows={4} dir="auto" />
        <div>
          <Button variant="secondary" size="sm" loading={update.isPending} onClick={() => save({ note: note.trim() || null }, "Note saved.")}>
            Save note
          </Button>
        </div>
      </section>

      <p className="text-[12px] text-ink-2">
        {lead.run ? `Found in run #${lead.run.number}. ` : lead.source === "chat" ? "Pushed from a chat. " : "Added by hand. "}
        {lead.touched ? "A person changed it, so Delete run keeps it." : "Nobody changed it yet."}
      </p>
      <div>
        <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
          Delete lead
        </Button>
      </div>
      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${lead.name}?`}
        confirmLabel="Delete lead"
        tone="danger"
        loading={remove.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() =>
          remove.mutate(lead.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              showToast("Lead deleted.");
              onDeleted();
            },
            onError,
          })
        }
      >
        <p>The lead, its numbers and its labels go. A later run can find the place again.</p>
      </ConfirmDialog>
    </div>
  );
}

// --- add a lead -------------------------------------------------------------------------------

function AddLeadPanel({ open, labels, onClose, onAdded }: { open: boolean; labels: AdminLeadLabel[]; onClose: () => void; onAdded: (lead: AdminLead) => void }) {
  return (
    <Panel open={open} onClose={onClose} title="Add a lead" subtitle="A place you know. It needs a phone number or an Instagram handle." width="lg">
      {open ? <AddLeadForm labels={labels} onDone={(lead) => (onClose(), onAdded(lead))} /> : null}
    </Panel>
  );
}

function AddLeadForm({ labels, onDone }: { labels: AdminLeadLabel[]; onDone: (lead: AdminLead) => void }) {
  const create = useCreateLead();
  const [name, setName] = useState("");
  const [sport, setSport] = useState<LeadSport>("swimming");
  const [emirate, setEmirate] = useState<UaeEmirate>("Dubai");
  const [area, setArea] = useState("");
  const [instagram, setInstagram] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [note, setNote] = useState("");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [tried, setTried] = useState(false);

  const nameError = tried && !name.trim() ? "Write the name of the place." : undefined;
  const contactError = tried && !instagram.trim() && !phone.trim() ? "Give a phone number or an Instagram handle." : undefined;
  const fitting = labels.filter((label) => labelFitsSport(label.sport, sport) && !label.systemKey);

  function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    if (!name.trim() || (!instagram.trim() && !phone.trim())) return;
    create.mutate(
      {
        name: name.trim(),
        sport,
        emirate,
        ...(area.trim() ? { area: area.trim() } : {}),
        ...(instagram.trim() ? { instagram: instagram.trim() } : {}),
        ...(website.trim() ? { website: website.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        phones: phone.trim() ? [{ number: phone.trim() }] : [],
        labelIds: [...chosen].filter((id) => fitting.some((label) => label.id === id)),
      },
      {
        onSuccess: (lead) => {
          showToast(`${lead.name} added. Score ${lead.score}.`);
          onDone(lead);
        },
      },
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      {create.error ? <InlineError error={create.error} /> : null}
      <Field label="Name" value={name} onChange={(event) => setName(event.target.value)} error={nameError} dir="auto" autoComplete="off" />
      <SegmentedControl
        label="Sport"
        value={sport}
        onChange={setSport}
        options={[
          { value: "swimming", label: "Swimming" },
          { value: "pilates", label: "Pilates" },
        ]}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField label="Emirate" value={emirate} onChange={(event) => setEmirate(event.target.value as UaeEmirate)}>
          {UAE_EMIRATES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </SelectField>
        <Field label="Area" hint="For example: Al Barsha, Mirdif." value={area} onChange={(event) => setArea(event.target.value)} dir="auto" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Phone number" type="tel" inputMode="tel" value={phone} onChange={(event) => setPhone(event.target.value)} error={contactError} />
        <Field label="Instagram" hint="A handle or a link." value={instagram} onChange={(event) => setInstagram(event.target.value)} autoComplete="off" />
      </div>
      <Field label="Website" type="url" inputMode="url" value={website} onChange={(event) => setWebsite(event.target.value)} />
      <TextAreaField label="Note" value={note} onChange={(event) => setNote(event.target.value)} rows={3} dir="auto" />
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-[14px] font-semibold text-ink">Labels</legend>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {fitting.map((label) => (
            <Checkbox
              key={label.id}
              size="compact"
              label={label.score !== null ? `${label.name} (${label.score > 0 ? "+" : ""}${label.score})` : label.name}
              checked={chosen.has(label.id)}
              onChange={(next) =>
                setChosen((current) => {
                  const copy = new Set(current);
                  if (next) copy.add(label.id);
                  else copy.delete(label.id);
                  return copy;
                })
              }
            />
          ))}
        </div>
      </fieldset>
      <div>
        <Button type="submit" loading={create.isPending}>
          Add lead
        </Button>
      </div>
    </form>
  );
}

